import { Component } from "@theme/component";
/**
 * Visibility Wrapper for TurnTo Teasers
 * Usage:
 * <tt-visibility-wrapper data-visibility="mobile_only" data-breakpoint="750">
 * <div></div>
 * </tt-visibility-wrapper>
 *
 * On breakpoint change, finds whichever wrapper instance currently holds a
 * populated #tt-teaser-widget (has children), moves those children into the
 * correct wrapper for the current viewport, and reassigns the id there.
 */
export class TtVisibilityWrapper extends Component {
  connectedCallback() {
    this.innerDiv = this.querySelector("div");
    this.breakpoint = parseInt(this.getAttribute("data-breakpoint")) || 750;
    this.visibility = this.getAttribute("data-visibility") || "both";

    this._mql = window.matchMedia(`(min-width: ${this.breakpoint}px)`);
    this._onBreakpoint = () => this._relocateWidget();
    this._mql.addEventListener("change", this._onBreakpoint);

    this._applyVisibility();
  }

  disconnectedCallback() {
    if (this._mql && this._onBreakpoint) {
      this._mql.removeEventListener("change", this._onBreakpoint);
    }
  }

  /**
   * Show/hide this wrapper based on its visibility setting and the current
   * viewport. Also assigns or removes the #tt-teaser-widget id on the inner div.
   */
  _applyVisibility() {
    if (!this.innerDiv) return;

    const isDesktop =
      this._mql != null
        ? this._mql.matches
        : window.innerWidth >= this.breakpoint;
    const shouldShow =
      this.visibility === "both" ||
      (this.visibility === "mobile_only" && !isDesktop) ||
      (this.visibility === "desktop_only" && isDesktop);

    if (shouldShow) {
      this.innerDiv.id = "tt-teaser-widget";
      this.style.display = "block";
    } else {
      this.innerDiv.removeAttribute("id");
      this.style.display = "none";
    }
  }

  /**
   * Called on every breakpoint crossing (via matchMedia).
   *
   * 1. Find all tt-visibility-wrapper instances on the page.
   * 2. Locate whichever one currently has a populated #tt-teaser-widget div
   *    (i.e. the div has child nodes from a previous TurnTo render).
   * 3. Determine the correct target wrapper for the new viewport.
   * 4. Move the children across and reassign #tt-teaser-widget to the target div.
   * 5. Update show/hide state on all instances.
   */
  _relocateWidget() {
    const isDesktop = this._mql.matches;
    const targetVisibility = isDesktop ? "desktop_only" : "mobile_only";

    const allWrappers = /** @type {TtVisibilityWrapper[]} */ (
      Array.from(document.querySelectorAll("tt-visibility-wrapper"))
    );

    const source = allWrappers.find((w) => {
      const div = w.querySelector("div");
      return div && div.childElementCount > 0;
    });

    const target = allWrappers.find(
      (w) => w.getAttribute("data-visibility") === targetVisibility,
    );

    if (source && target && source !== target) {
      const sourceDiv = source.querySelector("div");
      const targetDiv = target.querySelector("div");

      if (sourceDiv && targetDiv) {
        console.log(
          `[tt-visibility-wrapper] Moving TurnTo children from "${source.getAttribute("data-visibility")}" → "${targetVisibility}"`,
        );

        while (sourceDiv.firstChild) {
          targetDiv.appendChild(sourceDiv.firstChild);
        }

        sourceDiv.removeAttribute("id");
        targetDiv.id = "tt-teaser-widget";
      }
    } else if (!source) {
      console.log(
        "[tt-visibility-wrapper] No populated #tt-teaser-widget found — applying visibility only.",
      );
    }

    for (const w of allWrappers) {
      w._applyVisibility();
    }
  }
}

if (!customElements.get("tt-visibility-wrapper")) {
  customElements.define("tt-visibility-wrapper", TtVisibilityWrapper);
}
